import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'

import { queryKeys } from '@sportninja/common/config/queryKeys'
import type { SchedulePeriod } from '@sportninja/common/features/game/gameListView/calendarAnchor'
import { calendarAnchorForPeriod } from '@sportninja/common/features/game/gameListView/calendarAnchor'
import { paddedRequestWindow } from '@sportninja/common/features/game/gameListView/gameDayBucket'
import type { CivilDateRange } from '@sportninja/common/features/game/gameListView/types'
import { todayCivilDate } from '@sportninja/common/features/game/gameListView/venueLocalDateTime'
import { fetchGameDates } from '@sportninja/common/services/game/gameDatesService'

export interface UseCalendarAnchorArgs {
  /**
   * Only the bounded calendar layouts need this, and only while the user has
   * not picked a month of their own. Everything else passes `false` and the
   * request never goes out.
   */
  enabled: boolean
  period: SchedulePeriod
  /**
   * The period's own civil window — the very range the List view filters by, so
   * the month this lands on can only ever be a month List would also show.
   */
  range: CivilDateRange | null
  scheduleId?: string | null
  teamId?: string | null
}

export interface UseCalendarAnchorResult {
  /** The civil date to open on, or null to leave the default alone. */
  anchorDate: string | null
  /**
   * True while the answer is still in flight. Callers gate their own game query
   * on this: opening on today's month and then jumping is two fetches and a
   * visible flinch.
   */
  isResolving: boolean
}

/**
 * Where the calendar should OPEN, for the active Past / Today / Upcoming
 * selection.
 *
 * The calendar used to open on the current real-world month no matter what,
 * which put a team whose season ended in March on an empty August — and every
 * bounded calendar layout drives its own window, so the period control is
 * disabled there and the user had nothing to steer with but the month arrows.
 *
 * `TODAY` is answered without a request: the period is one day, and the month
 * containing it is the month to show whether or not it has games.
 */
export const useCalendarAnchor = ({
  enabled,
  period,
  range,
  scheduleId,
  teamId,
}: UseCalendarAnchorArgs): UseCalendarAnchorResult => {
  'use memo'

  const today = todayCivilDate()

  //  The zone the API groups the days in. Left off, `/games/dates` aggregates
  //  in UTC and an evening game lands on tomorrow — which at a month boundary
  //  is a different month to open on.
  const timeZone = DateTime.local().zoneName

  //  Padded exactly as the games query pads it. A civil day is not a UTC day,
  //  so asking for the bare bounds drops an evening game on the first day of
  //  the window and the day index would not know the month exists.
  const window =
    range === null
      ? { startsAfter: '', startsBefore: '' }
      : paddedRequestWindow(range)

  const needsDates = enabled && period !== 'TODAY' && range !== null

  const { data, isPending } = useQuery({
    queryKey: queryKeys.scheduleGameDates.byFingerprint({
      scheduleId: scheduleId ?? null,
      teamId: teamId ?? null,
      //  The CIVIL bounds are the identity, not the padded instants — the
      //  padding is derived, and keying on it would only make the key noisier.
      start: range?.start ?? null,
      endExclusive: range?.endExclusive ?? null,
      timeZone,
    }),
    queryFn: () =>
      fetchGameDates({
        scheduleId,
        teamId,
        startsAfter: window.startsAfter,
        startsBefore: window.startsBefore,
        timeZone,
      }),
    enabled: needsDates,
    //  A day index moves when games are rescheduled, not while someone is
    //  looking at a month. Re-asking on every remount would put a request in
    //  front of every single view switch.
    staleTime: 5 * 60 * 1000,
  })

  if (!enabled) {
    return { anchorDate: null, isResolving: false }
  }

  if (period === 'TODAY' || range === null) {
    return { anchorDate: today, isResolving: false }
  }

  //  `isPending` is React Query's "no data yet" — true for a disabled query as
  //  well, which is why it is read only on the path that actually asked.
  if (isPending) {
    return { anchorDate: null, isResolving: true }
  }

  return {
    anchorDate: calendarAnchorForPeriod(data ?? [], period, today),
    isResolving: false,
  }
}
